/**
 * Turn an endpoint create/update failure into something the User can see
 * (task 388, side-finding 1).
 *
 * Before this, a failed create was caught and written to `console.error`: the
 * form just sat there and the User had no idea the save had failed. The single
 * most common cause — a path already taken inside the account — was completely
 * invisible.
 *
 * Every failure now resolves to exactly one of two visible outcomes:
 *   - a **path-field error**, when the server rejected the path itself
 *     (duplicate within the account, or a reserved first segment — a 422 whose
 *     `validationErrors` name the `path` field);
 *   - a **toast**, for everything else (schema errors, plan limits, 5xx).
 *
 * The axios response interceptor (`src/services/api.ts`) already flattens the
 * response body onto the rejected Error: `.status` is the HTTP status, `.message`
 * is the server's `error`/`message` string, and `.details` is the body's
 * `details` object — which is where innovate-service puts `validationErrors`.
 */

import { friendlyApiErrorMessage } from '../../utils/composerErrors';

/** One `{ field, message }` entry from the service's `details.validationErrors`. */
interface ApiValidationError {
  field?: string;
  message?: string;
}

interface ApiErrorLike {
  status?: number;
  message?: string;
  details?: unknown;
  response?: unknown;
}

export interface EndpointSaveErrorDisplay {
  /** Inline error to show on the path field, when the path is what was rejected. */
  pathError?: string;
  /** Human-readable message for a toast. Always present. */
  message: string;
}

function validationErrorsOf(error: ApiErrorLike): ApiValidationError[] {
  const fromDetails = (error.details as { validationErrors?: unknown } | undefined)
    ?.validationErrors;
  const fromResponse = (
    error.response as { details?: { validationErrors?: unknown } } | undefined
  )?.details?.validationErrors;
  const raw = fromDetails ?? fromResponse;
  return Array.isArray(raw) ? (raw as ApiValidationError[]) : [];
}

/**
 * Classify a save failure. `fallback` names the operation for the generic case
 * (e.g. "Failed to create AI Service").
 */
export function describeEndpointSaveError(
  error: unknown,
  fallback = 'Failed to save AI Service'
): EndpointSaveErrorDisplay {
  const err = (error ?? {}) as ApiErrorLike;
  const serverMessage =
    typeof err.message === 'string' && err.message.trim() ? err.message.trim() : '';

  // Known backend codes (e.g. `no_active_plan`, task 465) get their friendly
  // translation instead of surfacing the raw code in a toast. The global
  // ChoosePlanPrompt also fires via the interceptor; this keeps the local
  // toast copy consistent with it.
  const friendly = friendlyApiErrorMessage(error);
  if (friendly) {
    return { message: friendly };
  }

  // A path-specific validation error, whatever the status (400 empty path,
  // 422 reserved first segment).
  const pathValidation = validationErrorsOf(err).find(v => v.field === 'path');
  if (pathValidation?.message) {
    return { pathError: pathValidation.message, message: pathValidation.message };
  }

  // Conflict: this account already has an AI Service on that path. Paths are
  // unique per account (not globally) since task 384, so the fix is always
  // "pick another path", never "wait for someone else to release it".
  if (err.status === 409) {
    const message =
      'That path is already used by another AI Service in your account. Choose a different path.';
    return { pathError: message, message };
  }

  return { message: serverMessage || fallback };
}
